import { env } from '@/env';
import { logger } from '@/shared/logging';

const log = logger('pendo');

const PENDO_AGENT_ID = 'pendo-agent';

// The install snippet stubs these before the agent script lands, queueing any
// early calls on `_q` for the real agent to replay once it loads.
const QUEUED_METHODS = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];

export type PendoVisitor = {
    id: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    role?: string;
};

export type PendoAccount = {
    id: string;
};

export type PendoIdentity = {
    visitor: PendoVisitor;
    account?: PendoAccount;
};

export const isPendoEnabled = () => Boolean(env.VUE_APP_PENDO_API_KEY)
    && env.VUE_APP_THIRD_PARTY_LOGGING !== 'disabled';

/**
 * Loads the Pendo agent. Safe to call more than once — the agent is only ever
 * attached to the page a single time.
 */
export function attachPendo() {
    if (!isPendoEnabled() || document.getElementById(PENDO_AGENT_ID)) {
        return;
    }

    try {
        const agent = window.pendo ?? {};

        window.pendo = agent;
        agent._q = agent._q ?? [];

        QUEUED_METHODS.forEach((method) => {
            agent[method] = agent[method] ?? ((...args: unknown[]) => {
                // initialize has to run ahead of whatever queued up behind it
                agent._q[method === 'initialize' ? 'unshift' : 'push']([method, ...args]);
            });
        });

        const tag = document.createElement('script');

        tag.id = PENDO_AGENT_ID;
        tag.async = true;
        tag.src = `https://cdn.pendo.io/agent/static/${env.VUE_APP_PENDO_API_KEY}/pendo.js`;
        document.head.appendChild(tag);
    } catch (e) {
        log.warn('Error attaching Pendo agent', e);
    }
}

/**
 * Identifies the current visitor. Pendo only honours the first call per page
 * load, so this must wait until the user and their account details are known.
 */
export function initializePendo(identity: PendoIdentity) {
    if (!isPendoEnabled()) {
        return;
    }

    try {
        log.info('initialize', identity);
        window.pendo?.initialize(identity);
    } catch (e) {
        log.warn('Error initializing Pendo', e);
    }
}
